import React, { useState } from "react";
import {
  Phone, PhoneDisconnect, Microphone, MicrophoneSlash,
  PauseCircle, PlayCircle, Backspace
} from "@phosphor-icons/react";

const DIALPAD = [
  ["1",""],["2","ABC"],["3","DEF"],
  ["4","GHI"],["5","JKL"],["6","MNO"],
  ["7","PQRS"],["8","TUV"],["9","WXYZ"],
  ["*",""],["0","+"],["#",""],
];

export default function MiniDialer({ dialerState, defaultNumber = "" }) {
  const [number, setNumber] = useState(defaultNumber);
  const [callerId, setCallerId] = useState("");
  const [showDialpad, setShowDialpad] = useState(false);

  const {
    status, isReady, isInCall, isMuted, isOnHold,
    formattedDuration, error, dial, hangup, toggleMute, toggleHold, sendDTMF
  } = dialerState || {};

  React.useEffect(() => {
    import("../services/api").then(({ default: API }) => {
      API.get("/twilio/credentials").then(({ data }) => {
        setCallerId(data.caller_id || "");
      }).catch(() => {});
    });
  }, []);

  React.useEffect(() => {
    if (defaultNumber && defaultNumber !== number) setNumber(defaultNumber);
  }, [defaultNumber]);

  const statusColor = { idle: isReady ? "var(--color-teal)" : "#535862", connecting: "var(--color-orange)", ringing: "var(--color-blue)", "in-call": "var(--color-teal)", error: "var(--color-danger)" }[status] || "#535862";
  const statusLabel = { idle: isReady ? "Ready" : "Connecting...", connecting: "Dialing...", ringing: "Ringing...", "in-call": formattedDuration || "In Call", error: "Error" }[status] || "—";

  const handleKey = (digit) => {
    if (isInCall) { sendDTMF?.(digit); return; }
    setNumber(n => n + digit);
  };

  return (
    <div style={{
      width: 220, flexShrink: 0, background: "#0f0f0f",
      borderRight: "1px solid var(--color-surface-mid)",
      display: "flex", flexDirection: "column",
      padding: "16px 12px", gap: 10, overflowY: "auto"
    }}>
      {/* Status */}
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
        <div style={{ width:7, height:7, borderRadius:"50%", background:statusColor, flexShrink:0 }}/>
        <span style={{ fontSize:11, color:statusColor, fontWeight:600 }}>{statusLabel}</span>
      </div>

      {error && <div style={{ fontSize:11, color:"var(--color-danger)", padding:"6px 8px", background:"rgba(224,82,82,0.1)", borderRadius:6 }}>{error}</div>}

      {/* Number input */}
      <div style={{ display:"flex", alignItems:"center", background:"var(--color-surface)", borderRadius:8, border:"1px solid var(--color-surface-mid)", overflow:"hidden" }}>
        <input
          value={number}
          onChange={e => !isInCall && setNumber(e.target.value)}
          placeholder="+1 555 000 0000"
          disabled={isInCall}
          style={{ flex:1, background:"none", border:"none", fontSize:12, padding:"8px 10px", fontFamily:"var(--font-mono)" }}
        />
        {number && !isInCall && (
          <button onClick={() => setNumber(n => n.slice(0,-1))}
            style={{ background:"none", border:"none", padding:"0 8px", color:"var(--color-gray)", cursor:"pointer" }}>
            <Backspace size={14}/>
          </button>
        )}
      </div>

      {/* Dialpad toggle */}
      <button onClick={() => setShowDialpad(s => !s)}
        style={{ background:"var(--color-surface)", border:"1px solid var(--color-surface-mid)", borderRadius:6, padding:"5px 10px", fontSize:11, color:"var(--color-gray)", cursor:"pointer", fontFamily:"var(--font-primary)" }}>
        {showDialpad ? "Hide Dialpad" : "Show Dialpad"}
      </button>

      {/* Mini dialpad */}
      {showDialpad && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:4 }}>
          {DIALPAD.map(([digit, letters]) => (
            <button key={digit} onClick={() => handleKey(digit)}
              style={{ background:"var(--color-surface)", border:"1px solid var(--color-surface-mid)", borderRadius:6, padding:"8px 4px", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:1 }}>
              <span style={{ fontSize:14, fontWeight:600, color:"var(--color-white)", fontFamily:"var(--font-primary)" }}>{digit}</span>
              {letters && <span style={{ fontSize:8, color:"var(--color-dark-gray)", letterSpacing:1 }}>{letters}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Call controls */}
      {!isInCall ? (
        <button className="btn btn-primary" style={{ width:"100%", gap:6 }}
          onClick={() => dial?.(number.trim(), callerId)}
          disabled={!isReady || !number.trim()}>
          <Phone size={15} weight="fill"/> Call
        </button>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={toggleMute}
              style={{ flex:1, background: isMuted ? "rgba(58,187,144,0.15)" : "var(--color-surface)", border:"1px solid var(--color-surface-mid)", borderRadius:6, padding:"8px 4px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:4, color: isMuted ? "var(--color-teal)" : "var(--color-gray)", fontSize:11, fontFamily:"var(--font-primary)" }}>
              {isMuted ? <MicrophoneSlash size={14}/> : <Microphone size={14}/>}
              {isMuted ? "Unmute" : "Mute"}
            </button>
            <button onClick={toggleHold}
              style={{ flex:1, background: isOnHold ? "rgba(236,108,33,0.15)" : "var(--color-surface)", border:"1px solid var(--color-surface-mid)", borderRadius:6, padding:"8px 4px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:4, color: isOnHold ? "var(--color-orange)" : "var(--color-gray)", fontSize:11, fontFamily:"var(--font-primary)" }}>
              {isOnHold ? <PlayCircle size={14}/> : <PauseCircle size={14}/>}
              {isOnHold ? "Resume" : "Hold"}
            </button>
          </div>
          <button onClick={hangup}
            style={{ width:"100%", background:"var(--color-danger)", border:"none", borderRadius:6, padding:"9px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, color:"white", fontSize:13, fontWeight:600, fontFamily:"var(--font-primary)" }}>
            <PhoneDisconnect size={15}/> End Call
          </button>
        </div>
      )}
    </div>
  );
}
